import { FC, useEffect, useMemo, useState } from 'react'
import CN from 'classnames'
import {
  useGetSsccXmlFixData,
  useRetriggerSsccXml,
} from 'framework/api/methods/ssccXml/useSsccXmlFix'

import { formatSsccXmlDate } from '../ssccXmlFormat'

import {
  analyse,
  cartonCube,
  FixCarton,
  FixHeader,
  FixLine,
  FixState,
  HEADER_FIELDS,
  nextId,
  nextSkuForCarton,
  skuOptions,
} from './fixSsccXmlRules'

export interface FixSsccXmlModalProps {
  validationId: string
  orderNumber: string
  onClose: () => void
  /** Called with the sent file name after a successful Save & Trigger. */
  onSent: (fileName: string) => void
}

const RED = '#D03A3A'
const inputBase =
  'h-8 w-full box-border border rounded px-2 text-[13px] text-[#2A2A2D] outline-none tabular-nums'
const errText = 'text-xs font-bold text-[#D03A3A]'

const str = (v: any) => (v === null || v === undefined ? '' : String(v))
const toInt = (v: string) => (v.trim() === '' ? null : Number(v))
const toDec = (v: string) => (v.trim() === '' ? null : Number(v))

const fieldClass = (error: boolean, readOnly = false) =>
  CN(inputBase, {
    'border-[#D03A3A] bg-[#FFF7F7]': error,
    'border-[#DADBDF] bg-white': !error && !readOnly,
    'border-[#E4E5E8] bg-[#F0F0F2] text-[#52525B]': readOnly && !error,
  })

/** BDL20-3995: Fix SSCC XML pop-up (design screen 3). */
export const FixSsccXmlModal: FC<FixSsccXmlModalProps> = ({
  validationId,
  orderNumber,
  onClose,
  onSent,
}: FixSsccXmlModalProps) => {
  const { data, isLoading, isError } = useGetSsccXmlFixData(validationId)
  const { mutate: retrigger, isLoading: isSending } = useRetriggerSsccXml()

  const [state, setState] = useState<FixState | null>(null)
  const [orderSkus, setOrderSkus] = useState<string[]>([])
  const [attempted, setAttempted] = useState(false)
  const [usedLabels, setUsedLabels] = useState<string[]>([])
  const [serverErrors, setServerErrors] = useState<string[]>([])
  const [openSku, setOpenSku] = useState<number | null>(null)

  useEffect(() => {
    if (!data) return
    const lines: FixLine[] = (data.lines ?? []).map((l: any) => ({
      id: nextId(),
      sku: str(l.sku),
      ordered: str(l.orderedQty),
      shipped: str(l.shippedQty),
      fromOrder: true,
    }))
    setOrderSkus(Array.from(new Set(lines.map((l) => l.sku).filter(Boolean))))
    const h = data.header ?? {}
    setState({
      header: {
        messageNo: str(h.messageNo),
        customer_id: str(h.customerId),
        order_number: str(h.orderNumber),
        connote: str(h.connote),
        orderdate: str(h.orderDate),
        carrier: str(h.carrier),
        order_currency_code: str(h.orderCurrencyCode),
        date_shipped: str(h.dateShipped),
      },
      lines,
      cartons: (data.cartons ?? []).map((c: any) => ({
        id: nextId(),
        label: str(c.labelId),
        l: str(c.lengthMm),
        w: str(c.widthMm),
        h: str(c.heightMm),
        kg: str(c.weightKg),
        items: (c.items ?? []).map((it: any) => ({
          id: nextId(),
          sku: str(it.sku),
          qty: str(it.qty),
        })),
      })),
    })
  }, [data])

  const a = useMemo(
    () => (state ? analyse(state, usedLabels) : null),
    [state, usedLabels],
  )

  const updateHeader = (key: keyof FixHeader, value: string) =>
    setState((s) => (s ? { ...s, header: { ...s.header, [key]: value } } : s))
  const updateLine = (id: number, patch: Partial<FixLine>) =>
    setState((s) =>
      s
        ? {
            ...s,
            lines: s.lines.map((l) => (l.id === id ? { ...l, ...patch } : l)),
          }
        : s,
    )
  const updateCarton = (id: number, patch: Partial<FixCarton>) =>
    setState((s) =>
      s
        ? {
            ...s,
            cartons: s.cartons.map((c) =>
              c.id === id ? { ...c, ...patch } : c,
            ),
          }
        : s,
    )

  const pickSku = (carton: FixCarton, itemId: number, sku: string) => {
    const items = carton.items.map((it) =>
      it.id === itemId ? { ...it, sku } : it,
    )
    // BA (2026-10-15): dimensions are not taken from product data; they stay as entered or as sent in the ASN.
    updateCarton(carton.id, { items })
    setOpenSku(null)
  }

  const addCarton = () =>
    setState((s) => {
      if (!s) return s
      const sku = s.lines.find((l) => l.sku)?.sku ?? ''
      return {
        ...s,
        cartons: [
          ...s.cartons,
          {
            id: nextId(),
            label: '',
            l: '',
            w: '',
            h: '',
            kg: '',
            items: [{ id: nextId(), sku, qty: '1' }],
          },
        ],
      }
    })

  const save = () => {
    if (!state || !a) return
    setServerErrors([])
    if (a.issues > 0) {
      setAttempted(true)
      return
    }
    const submission = {
      header: {
        messageNo: state.header.messageNo,
        customerId: state.header.customer_id,
        orderNumber: state.header.order_number,
        connote: state.header.connote,
        orderDate: state.header.orderdate,
        carrier: state.header.carrier,
        orderCurrencyCode: state.header.order_currency_code,
        dateShipped: state.header.date_shipped,
      },
      lines: state.lines.map((l) => ({
        sku: l.sku,
        orderedQty: toInt(l.ordered),
        shippedQty: toInt(l.shipped),
      })),
      cartons: state.cartons.map((c) => ({
        labelId: c.label.trim(),
        lengthMm: toDec(c.l),
        widthMm: toDec(c.w),
        heightMm: toDec(c.h),
        weightKg: toDec(c.kg),
        items: c.items.map((it) => ({ sku: it.sku, qty: toInt(it.qty) })),
      })),
    }
    retrigger(
      { validationId, submission },
      {
        onSuccess: ({ data: res }: any) => onSent(res?.fileName ?? ''),
        onError: (err: any) => {
          const errors: string[] = err?.response?.data?.errors ?? [
            err?.response?.data?.message ||
              'The SSCC XML could not be sent to T&W. Nothing was sent; please try again.',
          ]
          const labelSuffix = ': Label ID already used on another TWD order'
          setUsedLabels(
            errors
              .filter((e) => e.endsWith(labelSuffix))
              .map((e) => e.slice(0, -labelSuffix.length)),
          )
          setServerErrors(errors.filter((e) => !e.endsWith(labelSuffix)))
          setAttempted(true)
        },
      },
    )
  }

  const issuesText = !a
    ? ''
    : a.issues
    ? `${a.issues} issue${
        a.issues === 1 ? '' : 's'
      } to resolve before triggering`
    : 'All checks passed'
  let issuesColor = '#6B6F78'
  if (a && a.issues === 0) issuesColor = '#1F9D5B'
  else if (attempted) issuesColor = RED

  return (
    <div
      className='fixed inset-0 z-50'
      role='dialog'
      aria-modal='true'
      aria-labelledby='fix-sscc-xml-title'>
      <div className='absolute inset-0 bg-[rgba(20,20,26,0.5)]' />
      <div className='absolute inset-[5vh_5vw] bg-white rounded-xl flex flex-col shadow-2xl overflow-hidden'>
        <div className='flex items-center gap-3 px-6 py-4 border-b border-[#ECECEE]'>
          <h2
            id='fix-sscc-xml-title'
            className='m-0 flex-1 text-[19px] font-extrabold text-[#2A2A2D]'>
            Fix SSCC XML – {orderNumber}
          </h2>
          <button
            type='button'
            aria-label='Close'
            onClick={onClose}
            className='w-[30px] h-[30px] rounded-md bg-[#F0F0F2] flex items-center justify-center'>
            <i className='ri-close-line' />
          </button>
        </div>

        <div
          className='flex-1 min-h-0 overflow-y-auto px-6 pt-5 pb-10 flex flex-col gap-[26px]'
          onClick={() => openSku && setOpenSku(null)}
          role='presentation'>
          <div className='flex gap-3 items-stretch flex-wrap'>
            <div className='grid grid-cols-3 bg-[#F7F7F8] border border-[#ECECEE] rounded-lg'>
              {[
                ['Order Number', orderNumber],
                ['Order Date', formatSsccXmlDate(data?.orderDateUtc)],
                ['Carrier', 'TWD'],
              ].map(([k, v], i) => (
                <div
                  key={k}
                  className={CN('px-[18px] py-[10px] flex flex-col gap-[3px]', {
                    'border-l border-[#ECECEE]': i > 0,
                  })}>
                  <span className='text-[11px] font-extrabold tracking-wider uppercase text-[#6B6F78]'>
                    {k}
                  </span>
                  <span className='text-sm font-bold'>{v}</span>
                </div>
              ))}
            </div>
            <div className='flex-1 flex items-center gap-2 px-[14px] py-[10px] bg-[#FBEFD5] border border-[#F1D9A6] rounded-lg text-sm font-extrabold text-[#7A4E06]'>
              <i className='ri-error-warning-line text-[#B87400] text-base' />
              SSCC XML sending on hold
            </div>
          </div>

          {isLoading && (
            <p className='text-sm text-[#52525B]'>Loading order details…</p>
          )}
          {isError && (
            <p className='text-sm font-bold text-[#D03A3A]'>
              This order could not be loaded. It may already have been fixed.
              Close the pop-up and refresh the list.
            </p>
          )}

          {state && a && (
            <>
              {/* 1. Header Details */}
              <section className='flex flex-col gap-3'>
                <h3 className='m-0 text-base font-extrabold'>
                  1. Header Details
                </h3>
                <div className='grid grid-cols-4 gap-x-[18px] gap-y-[14px]'>
                  {HEADER_FIELDS.map((f) => {
                    const error = !!a.headerErrors[f.key]
                    const isDate = f.kind !== 'auto'
                    return (
                      <label
                        key={f.key}
                        htmlFor={`hdr-${f.key}`}
                        className='flex flex-col gap-[5px]'>
                        <span className='flex items-center gap-[6px] text-[13px] font-bold text-[#3F3F46] min-h-[20px]'>
                          <span className='font-mono text-[12.5px]'>
                            {f.label}
                          </span>
                          {f.kind === 'date' && (
                            <span className='text-[#D03A3A] -ml-1'>*</span>
                          )}
                          <span className='flex-1' />
                          {f.kind !== 'date' && (
                            <span className='px-[7px] py-px rounded-full bg-[#F0F0F2] text-[#52525B] text-[11px] font-bold'>
                              Auto
                            </span>
                          )}
                        </span>
                        <input
                          id={`hdr-${f.key}`}
                          type={isDate ? 'date' : 'text'}
                          value={state.header[f.key]}
                          onChange={(e) => updateHeader(f.key, e.target.value)}
                          className={CN(fieldClass(error), 'h-[38px] text-sm')}
                        />
                        {error && <span className={errText}>Required</span>}
                      </label>
                    )
                  })}
                  {[
                    ['cartons', String(state.cartons.length)],
                    ['totalweight (kg)', a.totalWeight.toFixed(1)],
                    ['totalcubic (m³)', a.totalCube.toFixed(3)],
                  ].map(([label, value]) => (
                    <div key={label} className='flex flex-col gap-[5px]'>
                      <span className='flex items-center gap-[6px] text-[13px] font-bold text-[#3F3F46] min-h-[20px]'>
                        <span className='font-mono text-[12.5px]'>{label}</span>
                        <span className='flex-1' />
                        <span className='px-[7px] py-px rounded-full bg-[#F0F0F2] text-[#52525B] text-[11px] font-bold'>
                          Calculated from cartons
                        </span>
                      </span>
                      <input
                        aria-label={label}
                        readOnly
                        value={value}
                        className={CN(
                          fieldClass(false, true),
                          'h-[38px] text-sm',
                        )}
                      />
                    </div>
                  ))}
                </div>
              </section>

              {/* 2. Order Lines */}
              <section className='flex flex-col gap-3'>
                <div className='flex items-center gap-[10px]'>
                  <h3 className='m-0 text-base font-extrabold'>
                    2. Order Lines
                  </h3>
                  <span className='flex-1 text-[12.5px] text-[#6B6F78]'>
                    Auto-populated from SO Create – editable
                  </span>
                  <button
                    type='button'
                    onClick={() =>
                      setState((s) =>
                        s
                          ? {
                              ...s,
                              lines: [
                                ...s.lines,
                                {
                                  id: nextId(),
                                  sku: '',
                                  ordered: '',
                                  shipped: '',
                                  fromOrder: false,
                                },
                              ],
                            }
                          : s,
                      )
                    }
                    className='inline-flex items-center gap-[6px] h-8 px-3 border border-[#F0A04B] rounded-md bg-white text-[#C47A2C] text-[13px] font-extrabold hover:bg-[#FDF1E3]'>
                    <i className='ri-add-line' />
                    Add Line
                  </button>
                </div>
                <div className='border border-[#ECECEE] rounded-md overflow-hidden'>
                  <div className='grid grid-cols-[90px_minmax(0,1fr)_180px_260px_56px] bg-[#F4F4F5] text-[13px] font-extrabold border-b border-[#E4E5E8]'>
                    <div className='px-[14px] py-[10px]'>Line No</div>
                    <div className='px-2 py-[10px]'>SKU</div>
                    <div className='px-2 py-[10px]'>Ordered Quantity</div>
                    <div className='px-2 py-[10px]'>Shipped Quantity</div>
                    <div />
                  </div>
                  {state.lines.map((line, i) => {
                    const e = a.lineErrors[line.id]
                    return (
                      <div
                        key={line.id}
                        className='grid grid-cols-[90px_minmax(0,1fr)_180px_260px_56px] items-start text-[13.5px] border-b border-[#F0F0F2]'>
                        <div className='px-[14px] py-[14px] text-[#52525B]'>
                          {i + 1}
                        </div>
                        <div className='px-2 py-[7px] flex flex-col gap-1'>
                          {line.fromOrder ? (
                            <input
                              aria-label='SKU'
                              readOnly
                              value={line.sku}
                              className={CN(
                                fieldClass(false, true),
                                'font-mono',
                              )}
                            />
                          ) : (
                            <select
                              aria-label='SKU'
                              value={line.sku}
                              onChange={(ev) =>
                                updateLine(line.id, { sku: ev.target.value })
                              }
                              className={CN(
                                fieldClass(!line.sku && attempted),
                                'font-mono',
                              )}>
                              <option value=''>Select SKU</option>
                              {orderSkus.map((sku) => (
                                <option key={sku} value={sku}>
                                  {sku}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                        <div className='px-2 py-[7px]'>
                          <input
                            aria-label='Ordered quantity'
                            readOnly={line.fromOrder}
                            value={line.ordered}
                            onChange={(ev) =>
                              updateLine(line.id, { ordered: ev.target.value })
                            }
                            className={fieldClass(false, line.fromOrder)}
                          />
                        </div>
                        <div className='px-2 py-[7px] flex flex-col gap-1'>
                          <input
                            aria-label='Shipped quantity'
                            value={line.shipped}
                            placeholder='Enter quantity'
                            onChange={(ev) =>
                              updateLine(line.id, { shipped: ev.target.value })
                            }
                            className={fieldClass(!!e)}
                          />
                          {e && <span className={errText}>{e}</span>}
                        </div>
                        <div className='pt-[9px] pr-[14px] pl-2 flex justify-end'>
                          <button
                            type='button'
                            title='Remove line'
                            onClick={() =>
                              setState((s) =>
                                s
                                  ? {
                                      ...s,
                                      lines: s.lines.filter(
                                        (x) => x.id !== line.id,
                                      ),
                                    }
                                  : s,
                              )
                            }
                            className='w-7 h-7 rounded-md bg-[#FBE4E4] text-[#D03A3A] flex items-center justify-center'>
                            <i className='ri-delete-bin-line' />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </section>

              {/* 3. Carton Details */}
              <section className='flex flex-col gap-3'>
                <div className='flex items-center gap-[10px]'>
                  <h3 className='m-0 flex-1 text-base font-extrabold'>
                    3. Carton Details
                  </h3>
                  <button
                    type='button'
                    onClick={addCarton}
                    className='inline-flex items-center gap-[6px] h-8 px-3 border border-[#F0A04B] rounded-md bg-white text-[#C47A2C] text-[13px] font-extrabold hover:bg-[#FDF1E3]'>
                    <i className='ri-add-line' />
                    Add Carton
                  </button>
                </div>
                <div className='border border-[#ECECEE] rounded-md'>
                  <div className='grid grid-cols-[80px_360px_minmax(0,1fr)_100px_100px_100px_100px_100px_52px] bg-[#F4F4F5] text-[13px] font-extrabold border-b border-[#E4E5E8] rounded-t-md'>
                    <div className='px-[14px] pt-2 pb-[10px]'>Carton No</div>
                    <div className='px-2 pt-2 pb-[10px] flex gap-[6px]'>
                      <span className='flex-1'>SKU</span>
                      <span className='w-16'>Quantity</span>
                      <span className='w-7' />
                    </div>
                    <div className='px-2 pt-2 pb-[10px]'>
                      Label ID<span className='text-[#D03A3A]'> *</span>
                    </div>
                    <div className='px-2 pt-2 pb-[10px]'>Length (mm)</div>
                    <div className='px-2 pt-2 pb-[10px]'>Width (mm)</div>
                    <div className='px-2 pt-2 pb-[10px]'>Height (mm)</div>
                    <div className='px-2 pt-2 pb-[10px]'>Weight (kg)</div>
                    <div className='px-2 pt-2 pb-[10px]'>Cube (m³)</div>
                    <div />
                  </div>

                  {state.cartons.map((carton, ci) => {
                    const labelError = a.labelErrors[carton.id]
                    const dims = a.dimErrors[carton.id]
                    const lineSkus = Array.from(
                      new Set(state.lines.map((l) => l.sku).filter(Boolean)),
                    )
                    const canAddSku = lineSkus.some(
                      (k) => !carton.items.some((y) => y.sku === k),
                    )
                    const cube = cartonCube(carton)
                    return (
                      <div
                        key={carton.id}
                        className='grid grid-cols-[80px_360px_minmax(0,1fr)_100px_100px_100px_100px_100px_52px] items-start text-[13.5px] border-b border-[#F0F0F2]'>
                        <div className='p-[14px] text-[#52525B] font-bold'>
                          {ci + 1}
                        </div>
                        <div className='px-2 py-[7px] flex flex-col gap-[6px]'>
                          {carton.items.map((item) => {
                            const qe = a.qtyErrors[item.id]
                            const open = openSku === item.id
                            return (
                              <div
                                key={item.id}
                                className='flex flex-col gap-1'>
                                <div className='flex gap-[6px] items-center relative'>
                                  <button
                                    type='button'
                                    aria-haspopup='listbox'
                                    aria-expanded={open}
                                    onClick={(ev) => {
                                      ev.stopPropagation()
                                      setOpenSku(open ? null : item.id)
                                    }}
                                    className={CN(
                                      'flex-1 min-w-0 h-8 flex items-center gap-[6px] border rounded px-2 bg-white font-mono text-[12.5px] text-left',
                                      {
                                        'border-[#F0A04B]': open,
                                        'border-[#DADBDF]': !open && !!item.sku,
                                        'border-[#D03A3A]': !item.sku,
                                      },
                                    )}>
                                    <span className='flex-1 truncate'>
                                      {item.sku || 'Select SKU'}
                                    </span>
                                    <i className='ri-arrow-down-s-line text-[#6B6F78]' />
                                  </button>
                                  <input
                                    aria-label={`Quantity for ${
                                      item.sku || 'SKU'
                                    }`}
                                    value={item.qty}
                                    placeholder='Enter qty'
                                    onChange={(ev) =>
                                      updateCarton(carton.id, {
                                        items: carton.items.map((it) =>
                                          it.id === item.id
                                            ? { ...it, qty: ev.target.value }
                                            : it,
                                        ),
                                      })
                                    }
                                    className={CN(fieldClass(!!qe), 'w-16')}
                                  />
                                  <button
                                    type='button'
                                    title='Remove SKU from carton'
                                    onClick={() =>
                                      updateCarton(carton.id, {
                                        items: carton.items.filter(
                                          (it) => it.id !== item.id,
                                        ),
                                      })
                                    }
                                    className={CN(
                                      'w-7 h-7 shrink-0 border border-[#E4E5E8] rounded-md bg-white text-[#6B6F78] flex items-center justify-center hover:text-[#D03A3A]',
                                      {
                                        invisible: carton.items.length <= 1,
                                      },
                                    )}>
                                    <i className='ri-close-line' />
                                  </button>
                                  {open && (
                                    <div
                                      role='listbox'
                                      className='absolute top-[38px] left-0 w-[280px] bg-white border border-[#DADBDF] rounded-md shadow-lg z-10 p-1 flex flex-col'>
                                      {skuOptions(state, a, carton, item).map(
                                        (o) => (
                                          <button
                                            key={o.sku}
                                            type='button'
                                            role='option'
                                            aria-selected={o.sku === item.sku}
                                            disabled={o.disabled}
                                            onClick={(ev) => {
                                              ev.stopPropagation()
                                              if (!o.disabled)
                                                pickSku(carton, item.id, o.sku)
                                            }}
                                            className={CN(
                                              'flex flex-col gap-px px-[10px] py-2 rounded text-left',
                                              {
                                                'bg-[#FDF1E3]':
                                                  o.sku === item.sku,
                                                'cursor-not-allowed':
                                                  o.disabled,
                                              },
                                            )}>
                                            <span
                                              className={CN(
                                                'font-mono text-[12.5px] font-bold',
                                                o.disabled
                                                  ? 'text-[#A1A1AA]'
                                                  : 'text-[#2A2A2D]',
                                              )}>
                                              {o.sku}
                                            </span>
                                            <span
                                              className={CN(
                                                'text-[11.5px]',
                                                o.disabled
                                                  ? 'text-[#A1A1AA]'
                                                  : 'text-[#6B6F78]',
                                              )}>
                                              {o.note}
                                            </span>
                                          </button>
                                        ),
                                      )}
                                    </div>
                                  )}
                                </div>
                                {qe && (
                                  <span
                                    className={CN(
                                      errText,
                                      'self-end mr-[34px]',
                                    )}>
                                    {qe}
                                  </span>
                                )}
                              </div>
                            )
                          })}
                          {canAddSku && (
                            <button
                              type='button'
                              onClick={() =>
                                updateCarton(carton.id, {
                                  items: [
                                    ...carton.items,
                                    {
                                      id: nextId(),
                                      sku: nextSkuForCarton(state, a, carton),
                                      qty: '1',
                                    },
                                  ],
                                })
                              }
                              className='self-start inline-flex items-center gap-[5px] py-[2px] text-[#C47A2C] text-[12.5px] font-extrabold hover:text-[#A8631C]'>
                              <i className='ri-add-line' />
                              Add SKU to carton
                            </button>
                          )}
                        </div>
                        <div className='px-2 py-[7px] flex flex-col gap-1'>
                          <input
                            aria-label={`Label ID for carton ${ci + 1}`}
                            value={carton.label}
                            placeholder='Enter label ID'
                            onChange={(ev) =>
                              updateCarton(carton.id, {
                                label: ev.target.value,
                              })
                            }
                            className={CN(
                              fieldClass(!!labelError),
                              'font-mono',
                            )}
                          />
                          {labelError && (
                            <span className={errText}>{labelError}</span>
                          )}
                        </div>
                        {(['l', 'w', 'h', 'kg'] as const).map((k) => (
                          <div
                            key={k}
                            className='px-2 py-[7px] flex flex-col gap-1'>
                            <input
                              aria-label={`${k} for carton ${ci + 1}`}
                              value={carton[k]}
                              onChange={(ev) =>
                                updateCarton(carton.id, {
                                  [k]: ev.target.value,
                                })
                              }
                              className={fieldClass(!!dims?.[k])}
                            />
                            {dims?.[k] && (
                              <span className={errText}>{dims[k]}</span>
                            )}
                          </div>
                        ))}
                        <div className='px-2 py-[7px]'>
                          <input
                            aria-label={`Cube for carton ${ci + 1}`}
                            readOnly
                            value={cube ? cube.toFixed(3) : ''}
                            className={fieldClass(false, true)}
                          />
                        </div>
                        <div className='pt-[9px] pr-[14px] pl-2 flex justify-end'>
                          <button
                            type='button'
                            title='Remove carton'
                            onClick={() =>
                              setState((s) =>
                                s
                                  ? {
                                      ...s,
                                      cartons: s.cartons.filter(
                                        (x) => x.id !== carton.id,
                                      ),
                                    }
                                  : s,
                              )
                            }
                            className='w-7 h-7 rounded-md bg-[#FBE4E4] text-[#D03A3A] flex items-center justify-center'>
                            <i className='ri-delete-bin-line' />
                          </button>
                        </div>
                      </div>
                    )
                  })}

                  <div className='flex items-center gap-5 px-[14px] py-3 bg-[#FAFAFB] rounded-b-md text-[13.5px] tabular-nums'>
                    <span className='flex-1 font-bold text-[#3F3F46]'>
                      Cartons: {state.cartons.length} · Total weight:{' '}
                      {a.totalWeight.toFixed(1)} kg · Total cube:{' '}
                      {a.totalCube.toFixed(3)} m³
                    </span>
                    <span
                      className='flex items-center gap-[6px] font-extrabold'
                      style={{ color: a.reconOk ? '#1F9D5B' : RED }}>
                      Allocated / Shipped units: {a.allocTotal} /{' '}
                      {a.shippedTotal}
                      {a.reconOk && <i className='ri-check-line' />}
                    </span>
                  </div>
                </div>
              </section>

              {serverErrors.length > 0 && (
                <div
                  role='alert'
                  className='border border-[#F3C4C4] bg-[#FFF7F7] rounded-md px-4 py-3 text-[13px] text-[#D03A3A] font-bold flex flex-col gap-1'>
                  {serverErrors.map((e) => (
                    <span key={e}>{e}</span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className='flex items-center gap-3 px-6 py-3 border-t border-[#ECECEE] bg-white'>
          <span
            className='flex-1 text-[13.5px] font-bold'
            style={{ color: issuesColor }}>
            {issuesText}
          </span>
          <button
            type='button'
            onClick={onClose}
            className='h-[42px] px-7 rounded bg-[#F2F2F4] text-[#2A2A2D] text-[15px] font-bold hover:bg-[#E8E8EB]'>
            Close
          </button>
          <button
            type='button'
            onClick={save}
            disabled={!state || isSending}
            className='h-[42px] px-6 rounded bg-[#F0A04B] text-white text-[15px] font-extrabold hover:bg-[#E8923A] disabled:opacity-60'>
            {isSending ? 'Sending…' : 'Save & Trigger SSCC XML'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default FixSsccXmlModal
